import * as THREE from 'three'
import {
  type GSplatClipState,
  isGSplatCenterVisible,
} from '@/plugins/gsplat/clipping/GSplatClipState'
import type { GSplatInfo } from '@/plugins/gsplat/lod/GSplatInfo'
import type { GSplatManager } from '@/plugins/gsplat/lod/GSplatManager'
import { getLoggerManager } from '@/shared/logging'
import {
  assertValidPickMinDistance,
  getPickRayDistance,
  isPickPointAtOrBeyondMinDistance,
} from '@/shared/utils/pickDepth'
import {
  type GpuPickResult,
  GSplatGpuPicker,
  type GSplatGpuPickerDiagnostics,
} from './GSplatGpuPicker'
import type { GSplatPickResult } from './GSplatPicker'

const log = getLoggerManager().getLogger('gsplat:spatial')

export enum InteractionType {
  CLICK = 'click',
  MOVE = 'move',
  AUTO = 'auto',
}

type PickInteractionType = InteractionType | 'click' | 'move' | 'auto'
type NormalizedInteractionType = 'click' | 'move' | 'auto' | undefined

export interface PickResult {
  location: THREE.Vector3
  distance: number
  splatIndex: number
  splatInfo: GSplatInfo
  normal?: THREE.Vector3
  depth?: number
  gpuPickResult?: GpuPickResult
  cpuPickResult?: GSplatPickResult
}

export interface PickOptions {
  interactionType?: PickInteractionType
  centerSphereRadius?: number
  gpuOnly?: boolean
  prewarmGpu?: boolean
  windowSize?: number
  screenX?: number
  screenY?: number
  screenWidth?: number
  screenHeight?: number
  layerMask?: number
  /** 从原始射线起点沿归一化方向计算的最小拾取距离。 */
  minDistance?: number
  /** unified LOD polygon 裁切快照，仅供内部 CPU fallback 使用。 */
  clipState?: GSplatClipState
}

interface GpuPickAttempt {
  result: PickResult | null
  unavailable: boolean
}

/** 渲染器级 GPU Picker 缓存的只读诊断快照。 */
export interface GSplatGpuPickerCacheDiagnostics extends GSplatGpuPickerDiagnostics {
  /** 当前渲染器是否仍关联 GPU Picker。 */
  readonly exists: boolean
}

export class GSplatPickManager {
  private static gpuPickerCache = new WeakMap<THREE.WebGLRenderer, GSplatGpuPicker>()
  private static gpuPickerStateVersionCache = new WeakMap<GSplatManager, string>()

  static pick(
    ray: THREE.Ray,
    camera: THREE.Camera,
    renderer: THREE.WebGLRenderer,
    managers: GSplatManager[],
    options: PickOptions = {}
  ): PickResult | null {
    assertValidPickMinDistance(options.minDistance)
    const gpuAttempt = GSplatPickManager.pickWithGpu(ray, camera, renderer, managers, options)
    if (gpuAttempt.result) {
      return gpuAttempt.result
    }

    if (options.gpuOnly) {
      return null
    }

    return GSplatPickManager.pickWithCpuCenter(ray, managers, options)
  }

  private static pickWithGpu(
    ray: THREE.Ray,
    camera: THREE.Camera,
    renderer: THREE.WebGLRenderer,
    managers: GSplatManager[],
    options: PickOptions = {}
  ): GpuPickAttempt {
    let unavailable = false

    for (const manager of managers) {
      if (manager.isScalarFilterActive && manager.scalarTextureVersion <= 0) {
        unavailable = true
        continue
      }

      const activeSplatInfos = GSplatPickManager.filterSplatInfosByLayerMask(
        manager.getActiveSplatInfos(),
        options.layerMask
      )
      if (activeSplatInfos.length === 0) {
        continue
      }

      try {
        const gpuPicker = GSplatPickManager.getGpuPicker(renderer, options)
        const currentVersion = GSplatPickManager.buildPickStateVersion(
          manager,
          options.clipState?.version
        )
        const lastVersion = GSplatPickManager.gpuPickerStateVersionCache.get(manager)
        const needsOnDemandPrepare =
          currentVersion !== lastVersion ||
          !GSplatPickManager.isGpuPickerPreparedForQuery(gpuPicker, camera, ray, options) ||
          !gpuPicker.isReady()

        if (needsOnDemandPrepare) {
          if (!GSplatPickManager.shouldPrepareGpuOnDemand(options)) {
            return { result: null, unavailable: true }
          }

          GSplatPickManager.prepareGpuPickerForQuery(
            gpuPicker,
            camera,
            manager,
            activeSplatInfos,
            ray,
            options
          )
          GSplatPickManager.markGpuPickerPrepared(manager, options.clipState?.version)
        }

        const { screenX, screenY, screenWidth, screenHeight } =
          GSplatPickManager.resolveScreenCoords(ray, camera, renderer, options)
        const gpuResult = GSplatPickManager.pickBestGpuResult(
          gpuPicker,
          camera,
          screenX,
          screenY,
          screenWidth,
          screenHeight,
          ray,
          options
        )
        if (!gpuResult) {
          continue
        }

        const worldPos = gpuResult.worldPosition.clone()
        if (options.clipState && !isGSplatCenterVisible(options.clipState, worldPos)) {
          unavailable = true
          continue
        }

        return {
          result: {
            location: worldPos,
            distance: getPickRayDistance(ray, worldPos),
            splatIndex: gpuResult.splatIndex,
            splatInfo: gpuResult.splatInfo,
            depth: gpuResult.depth,
            gpuPickResult: gpuResult,
          },
          unavailable: false,
        }
      } catch (error) {
        log.warn(
          '[GSplatPickManager] GPU pick failed: ' +
            (error instanceof Error ? error.message : String(error))
        )
        unavailable = true
      }
    }

    return { result: null, unavailable }
  }

  private static pickWithCpuCenter(
    ray: THREE.Ray,
    managers: GSplatManager[],
    options: PickOptions = {}
  ): PickResult | null {
    const allSplatInfos: GSplatInfo[] = []
    const managerBySplatInfo = new Map<GSplatInfo, GSplatManager>()
    for (const manager of managers) {
      for (const splatInfo of GSplatPickManager.filterSplatInfosByLayerMask(
        manager.getActiveSplatInfos(),
        options.layerMask
      )) {
        allSplatInfos.push(splatInfo)
        managerBySplatInfo.set(splatInfo, manager)
      }
    }

    if (allSplatInfos.length === 0) {
      return null
    }

    const result = GSplatGpuPicker.pickWithCenters(
      ray,
      allSplatInfos,
      options.centerSphereRadius ?? 0.02,
      1,
      (splatInfo, splatIndex, worldPosition) => {
        const manager = managerBySplatInfo.get(splatInfo)
        if (manager && !manager.passesScalarFilter(splatInfo, splatIndex)) {
          return false
        }
        return (
          isPickPointAtOrBeyondMinDistance(ray, worldPosition, options.minDistance) &&
          (!options.clipState || isGSplatCenterVisible(options.clipState, worldPosition))
        )
      }
    )[0]

    if (!result) {
      return null
    }

    const worldPos = result.worldPosition.clone()
    return {
      location: worldPos,
      distance: getPickRayDistance(ray, worldPos),
      splatIndex: result.splatIndex,
      splatInfo: result.splatInfo,
      depth: result.depth,
    }
  }

  private static getGpuPicker(
    renderer: THREE.WebGLRenderer,
    options: PickOptions = {}
  ): GSplatGpuPicker {
    let picker = GSplatPickManager.gpuPickerCache.get(renderer)
    const { width, height } = GSplatPickManager.resolveScreenSize(renderer, options)
    const pickerWidth = Math.ceil(width * 0.5)
    const pickerHeight = Math.ceil(height * 0.5)

    if (!picker) {
      picker = new GSplatGpuPicker(renderer, {
        width: pickerWidth,
        height: pickerHeight,
        alphaClip: 0.3,
      })
      GSplatPickManager.gpuPickerCache.set(renderer, picker)
    } else {
      picker.resize(pickerWidth, pickerHeight)
    }

    return picker
  }

  private static filterSplatInfosByLayerMask(
    splatInfos: GSplatInfo[],
    layerMask?: number
  ): GSplatInfo[] {
    if (layerMask === undefined) {
      return splatInfos
    }

    return splatInfos.filter((splatInfo) =>
      GSplatPickManager.matchesLayerMask(splatInfo, layerMask)
    )
  }

  private static matchesLayerMask(splatInfo: GSplatInfo, layerMask: number): boolean {
    const object = splatInfo.node as unknown as THREE.Object3D | undefined
    const objectLayerMask = object?.layers?.mask
    if (typeof objectLayerMask !== 'number') {
      return true
    }

    return (objectLayerMask & layerMask) !== 0
  }

  private static resolveScreenCoords(
    ray: THREE.Ray,
    camera: THREE.Camera,
    renderer: THREE.WebGLRenderer,
    options: PickOptions
  ): {
    screenX: number
    screenY: number
    screenWidth: number
    screenHeight: number
  } {
    if (Number.isFinite(options.screenX) && Number.isFinite(options.screenY)) {
      const { width, height } = GSplatPickManager.resolveScreenSize(renderer, options)
      return {
        screenX: Math.max(0, Math.min(width - 1, options.screenX as number)),
        screenY: Math.max(0, Math.min(height - 1, options.screenY as number)),
        screenWidth: width,
        screenHeight: height,
      }
    }

    return GSplatPickManager.rayToScreenCoords(ray, camera, renderer)
  }

  private static resolveScreenSize(
    renderer: THREE.WebGLRenderer,
    options: PickOptions
  ): { width: number; height: number } {
    if (
      Number.isFinite(options.screenWidth) &&
      Number.isFinite(options.screenHeight) &&
      (options.screenWidth as number) > 0 &&
      (options.screenHeight as number) > 0
    ) {
      return {
        width: options.screenWidth as number,
        height: options.screenHeight as number,
      }
    }

    return renderer.getSize(new THREE.Vector2())
  }

  private static shouldPrepareGpuOnDemand(options: PickOptions): boolean {
    return (
      GSplatPickManager.normalizeInteractionType(options.interactionType) === 'click' ||
      options.prewarmGpu === true
    )
  }

  private static isGpuPickerPreparedForQuery(
    gpuPicker: GSplatGpuPicker,
    camera: THREE.Camera,
    ray: THREE.Ray,
    options: PickOptions
  ): boolean {
    return options.minDistance === undefined
      ? gpuPicker.isPreparedFor(camera)
      : gpuPicker.isPreparedFor(camera, ray, options.minDistance)
  }

  private static prepareGpuPickerForQuery(
    gpuPicker: GSplatGpuPicker,
    camera: THREE.Camera,
    manager: GSplatManager,
    splatInfos: GSplatInfo[],
    ray: THREE.Ray,
    options: PickOptions
  ): void {
    if (options.minDistance === undefined) {
      gpuPicker.prepare(camera, manager, splatInfos)
      return
    }
    gpuPicker.prepare(camera, manager, splatInfos, {
      ray,
      minDistance: options.minDistance,
    })
  }

  private static pickBestGpuResult(
    gpuPicker: GSplatGpuPicker,
    camera: THREE.Camera,
    screenX: number,
    screenY: number,
    screenWidth: number,
    screenHeight: number,
    ray: THREE.Ray,
    options: PickOptions
  ): GpuPickResult | null {
    const windowSize = GSplatPickManager.resolveGpuPickWindowSize(options)
    if (windowSize <= 1) {
      const result = gpuPicker.pick(screenX, screenY, screenWidth, screenHeight)
      return result && GSplatPickManager.isGpuResultVisible(result, ray, options) ? result : null
    }

    const rect = GSplatPickManager.resolveGpuPickRect(
      screenX,
      screenY,
      screenWidth,
      screenHeight,
      windowSize
    )
    const candidates = gpuPicker
      .pickRect(rect.x, rect.y, rect.width, rect.height, screenWidth, screenHeight)
      .filter((candidate) => GSplatPickManager.isGpuResultVisible(candidate, ray, options))
    return GSplatPickManager.selectClosestGpuResult(
      candidates,
      camera,
      screenX,
      screenY,
      screenWidth,
      screenHeight
    )
  }

  private static isGpuResultVisible(
    result: GpuPickResult,
    ray: THREE.Ray,
    options: PickOptions
  ): boolean {
    return (
      isPickPointAtOrBeyondMinDistance(ray, result.worldPosition, options.minDistance) &&
      (!options.clipState || isGSplatCenterVisible(options.clipState, result.worldPosition))
    )
  }

  private static buildPickStateVersion(manager: GSplatManager, clipStateVersion = 0): string {
    return `${manager.pickStateVersion}|${clipStateVersion}`
  }

  private static resolveGpuPickWindowSize(options: PickOptions): number {
    const interactionType = GSplatPickManager.normalizeInteractionType(options.interactionType)
    if (Number.isFinite(options.windowSize)) {
      return Math.max(1, Math.floor(options.windowSize as number))
    }

    if (interactionType === 'click') {
      return 5
    }

    if (interactionType === 'move') {
      return 3
    }

    return 1
  }

  private static normalizeInteractionType(
    interactionType?: PickInteractionType
  ): NormalizedInteractionType {
    if (interactionType == null) {
      return undefined
    }

    return String(interactionType) as NormalizedInteractionType
  }

  private static resolveGpuPickRect(
    screenX: number,
    screenY: number,
    screenWidth: number,
    screenHeight: number,
    windowSize: number
  ): {
    x: number
    y: number
    width: number
    height: number
  } {
    const halfWindow = Math.floor(windowSize / 2)
    const x = Math.max(0, Math.floor(screenX - halfWindow))
    const y = Math.max(0, Math.floor(screenY - halfWindow))
    const maxX = Math.min(screenWidth, Math.ceil(screenX + halfWindow + 1))
    const maxY = Math.min(screenHeight, Math.ceil(screenY + halfWindow + 1))

    return {
      x,
      y,
      width: Math.max(1, maxX - x),
      height: Math.max(1, maxY - y),
    }
  }

  private static selectClosestGpuResult(
    candidates: GpuPickResult[],
    camera: THREE.Camera,
    screenX: number,
    screenY: number,
    screenWidth: number,
    screenHeight: number
  ): GpuPickResult | null {
    if (candidates.length === 0) {
      return null
    }

    let bestResult: GpuPickResult | null = null
    let bestScreenDistanceSq = Number.POSITIVE_INFINITY
    let bestDepth = Number.POSITIVE_INFINITY

    for (const candidate of candidates) {
      const projected = candidate.worldPosition.clone().project(camera)
      if (
        !Number.isFinite(projected.x) ||
        !Number.isFinite(projected.y) ||
        !Number.isFinite(projected.z)
      ) {
        continue
      }

      const projectedScreenX = ((projected.x + 1) / 2) * screenWidth
      const projectedScreenY = ((-projected.y + 1) / 2) * screenHeight
      const dx = projectedScreenX - screenX
      const dy = projectedScreenY - screenY
      const screenDistanceSq = dx * dx + dy * dy
      const depth = candidate.depth ?? camera.position.distanceToSquared(candidate.worldPosition)

      if (
        screenDistanceSq < bestScreenDistanceSq ||
        (screenDistanceSq === bestScreenDistanceSq && depth < bestDepth)
      ) {
        bestResult = candidate
        bestScreenDistanceSq = screenDistanceSq
        bestDepth = depth
      }
    }

    return bestResult ?? candidates[0] ?? null
  }

  private static rayToScreenCoords(
    ray: THREE.Ray,
    camera: THREE.Camera,
    renderer: THREE.WebGLRenderer
  ): {
    screenX: number
    screenY: number
    screenWidth: number
    screenHeight: number
  } {
    const { width, height } = renderer.getSize(new THREE.Vector2())
    const point = ray.at(1, new THREE.Vector3())
    point.project(camera)

    let screenX = ((point.x + 1) / 2) * width
    let screenY = ((-point.y + 1) / 2) * height
    screenX = Math.max(0, Math.min(width - 1, screenX))
    screenY = Math.max(0, Math.min(height - 1, screenY))

    return {
      screenX,
      screenY,
      screenWidth: width,
      screenHeight: height,
    }
  }

  static disposeGpuPicker(renderer: THREE.WebGLRenderer): void {
    const picker = GSplatPickManager.gpuPickerCache.get(renderer)
    if (picker) {
      picker.dispose()
      GSplatPickManager.gpuPickerCache.delete(renderer)
    }
  }

  /** 获取渲染器级 GPU Picker 缓存的只读诊断快照。 */
  static getGpuPickerDiagnostics(
    renderer: THREE.WebGLRenderer
  ): Readonly<GSplatGpuPickerCacheDiagnostics> {
    const picker = GSplatPickManager.gpuPickerCache.get(renderer)
    if (!picker) {
      return {
        exists: false,
        currentInfos: 0,
        pickMaterials: 0,
      }
    }

    return {
      exists: true,
      ...picker.getDiagnostics(),
    }
  }

  static clearGpuPickerLodVersion(manager: GSplatManager): void {
    GSplatPickManager.gpuPickerStateVersionCache.delete(manager)
  }

  /** 登记 picker 已完成指定 LOD 与裁切版本的 prepare。 */
  static markGpuPickerPrepared(manager: GSplatManager, clipStateVersion = 0): void {
    GSplatPickManager.gpuPickerStateVersionCache.set(
      manager,
      GSplatPickManager.buildPickStateVersion(manager, clipStateVersion)
    )
  }

  static clearAllGpuPickerLodVersions(): void {
    GSplatPickManager.gpuPickerStateVersionCache = new WeakMap<GSplatManager, string>()
  }
}
